import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthRepository } from '../../domain/repository';
import { AuthMapper } from '../mapper';
import { ApiResponse } from '../../../../shared/dto/response/api-response.dto';
import { getApiBaseUrl } from '../../../../shared/config/runtime-config';
import {
  AuthChangePasswordRequest,
  AuthChangePasswordResponse,
  AuthForgotPasswordRequest,
  AuthForgotPasswordResponse,
  AuthLoginRequest,
  AuthLoginResponse,
  AuthLogoutResponse,
  AuthLogoutRequest,
  AuthRefreshRequest,
  AuthRefreshResponse,
} from '../../domain/dto';
import { ModuleFlow, handleError } from '../../../../shared/utils/error.utils';

@Injectable({ providedIn: 'root' })
export class AuthRepositoryImpl implements AuthRepository {
  private readonly cookieConsentHeader = 'X-Cookie-Consent';
  private readonly cookieConsentKey = 'cookie_consent_status';

  constructor(
    private http: HttpClient,
    private mapper: AuthMapper,
  ) {}

  login(credentials: AuthLoginRequest): Observable<HttpResponse<AuthLoginResponse>> {
    let headers = new HttpHeaders();
    if (typeof localStorage !== 'undefined') {
      const consent = localStorage.getItem(this.cookieConsentKey) ?? '';
      if (consent) {
        headers = headers.set(this.cookieConsentHeader, consent);
      }
    }

    return this.http
      .post<ApiResponse<AuthLoginResponse>>(`${this.apiUrl}/login`, credentials, {
        observe: 'response',
        headers,
      })
      .pipe(
        map((response) => {
          const profile = response.body?.data?.profile;
          if (!profile) {
            throw new Error('AUTH_LOGIN_PROFILE_MISSING');
          }

          const body = this.mapper.toDomain(response.body.data);
          return response.clone({ body });
        }),
        catchError((error) => handleError(ModuleFlow.SignIn, error)),
      );
  }

  forgotPassword(
    payload: AuthForgotPasswordRequest,
  ): Observable<HttpResponse<AuthForgotPasswordResponse>> {
    return this.http
      .post<ApiResponse<AuthForgotPasswordResponse>>(`${this.apiUrl}/forgot-password`, payload, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: response.body?.data })),
        catchError((error) => handleError(ModuleFlow.ForgotPassword, error)),
      );
  }

  changePassword(
    request: AuthChangePasswordRequest,
  ): Observable<HttpResponse<AuthChangePasswordResponse>> {
    const { recoveryToken, ...payload } = request;
    let headers = new HttpHeaders();
    if (recoveryToken) {
      headers = headers.set('X-Recovery-Token', recoveryToken);
    }

    return this.http
      .post<ApiResponse<AuthChangePasswordResponse>>(`${this.apiUrl}/change-password`, payload, {
        observe: 'response',
        headers,
      })
      .pipe(
        map((response) => response.clone({ body: response.body?.data })),
        catchError((error) => handleError(ModuleFlow.ChangePassword, error)),
      );
  }

  refreshToken(_request: AuthRefreshRequest): Observable<HttpResponse<AuthRefreshResponse>> {
    return this.http
      .post<
        ApiResponse<AuthRefreshResponse>
      >(`${this.apiUrl}/refresh`, {}, { observe: 'response' })
      .pipe(
        map((response) => response.clone({ body: (response.body?.data ?? {}) as AuthRefreshResponse })),
        catchError((error) => handleError(ModuleFlow.RefreshToken, error)),
      );
  }

  logout(_request: AuthLogoutRequest): Observable<HttpResponse<AuthLogoutResponse>> {
    return this.http
      .post<ApiResponse<AuthLogoutResponse>>(`${this.apiUrl}/logout`, {}, { observe: 'response' })
      .pipe(
        map((response) => response.clone({ body: response.body?.data ?? {} })),
        catchError((error) => handleError(ModuleFlow.Logout, error)),
      );
  }

  private get apiUrl(): string {
    return getApiBaseUrl('auth');
  }
}


